import { EventEmitter } from '../../core/Event';

export interface WheelGameModelConfig {
  startingBalance: number;
  betPerSpin: number;
}

export type WheelBalanceChangeReason = 'spin-bet' | 'spin-win' | 'bankrupt';

export type WheelGameEvent =
  | { kind: 'balance-changed'; before: number; after: number; reason: WheelBalanceChangeReason }
  | { kind: 'spin-started'; bet: number }
  | { kind: 'spin-landed'; winnings: number; isBankrupt: boolean }
  | { kind: 'not-enough-coins'; required: number; have: number };

/**
 * Authoritative state for the wheel-of-fortune scene: balance + the fixed
 * per-spin bet. Wheel game flow is single-shot per round (debit → spin →
 * settle → credit), so the model is much simpler than {@link ../slot/SlotGameModel SlotGameModel}.
 *
 * <p>The bankrupt path zeroes balance independently of the bet — landing on
 * a bankrupt segment wipes everything, not just the bet. {@link bankrupt}
 * emits a {@code reason: 'bankrupt'} balance change so listeners can
 * distinguish from a normal spin-bet debit.
 */
export class WheelGameModel {
  public readonly events = new EventEmitter<WheelGameEvent>();
  public readonly betPerSpin: number;
  private _balance: number;

  constructor(config: WheelGameModelConfig) {
    this._balance = config.startingBalance;
    this.betPerSpin = config.betPerSpin;
  }

  get balance(): number {
    return this._balance;
  }

  /** Try to debit the bet. Emits {@code not-enough-coins} on failure and
   * returns false. On success, emits {@code balance-changed} +
   * {@code spin-started}. */
  tryDebitBet(): boolean {
    if (this._balance < this.betPerSpin) {
      this.events.emit({
        kind: 'not-enough-coins',
        required: this.betPerSpin,
        have: this._balance,
      });
      return false;
    }
    const before = this._balance;
    this._balance -= this.betPerSpin;
    this.events.emit({
      kind: 'balance-changed',
      before,
      after: this._balance,
      reason: 'spin-bet',
    });
    this.events.emit({ kind: 'spin-started', bet: this.betPerSpin });
    return true;
  }

  creditWinnings(winnings: number): void {
    if (winnings <= 0) return;
    const before = this._balance;
    this._balance += winnings;
    this.events.emit({
      kind: 'balance-changed',
      before,
      after: this._balance,
      reason: 'spin-win',
    });
  }

  bankrupt(): void {
    if (this._balance === 0) return;
    const before = this._balance;
    this._balance = 0;
    this.events.emit({
      kind: 'balance-changed',
      before,
      after: 0,
      reason: 'bankrupt',
    });
  }

  emitLanded(winnings: number, isBankrupt: boolean): void {
    this.events.emit({ kind: 'spin-landed', winnings, isBankrupt });
  }

  dispose(): void {
    this.events.clear();
  }
}
